import { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { ExchangeActionEnum } from "@b2bportal/air-exchange-api";
import {
  CoreFlightExchangeSteps,
  FlightsExchangeNavigationFlow,
  FlightExchangeSearchSelectors,
  FlightPostBookingSelectors,
  FlightSearchActions,
  FlightSearchSelectors,
  setOutboundSelection,
  setReturnSelection,
} from "@b2bportal/core-flights";
import { useFlightStyles, useModuleBEM } from "@b2bportal/core-themes";
import {
  CoreFlightsComponent,
  type PlatformComponentProps,
  type SliceOpt,
  TripCategory,
} from "@b2bportal/core-types";
import { Page } from "@b2bportal/core-ui";
import { FlightExchangeLanding, FlightSlicePickerModal } from "@tenant/flights";
import clsx from "clsx";

import defaultStyles from "./FlightExchangeSearch.module.scss";

export type FlightExchangeSearchComponentProps = PlatformComponentProps;

export const FlightExchangeSearch = ({
  className,
  children,
}: FlightExchangeSearchComponentProps) => {
  const COMPONENT_KEY = CoreFlightsComponent.FlightExchange;
  const styles = useFlightStyles(COMPONENT_KEY, defaultStyles);
  const [block, cn] = useModuleBEM(styles, COMPONENT_KEY);

  const [searchUpdated, setSearchUpdated] = useState(false);
  const [showSlicePicker, setShowSlicePicker] = useState(false);

  const dispatch = useDispatch();

  const selectedFlightItinerary = useSelector(
    FlightPostBookingSelectors.getSelectedFlightItinerary
  );
  const search = useSelector(FlightSearchSelectors.getSearchFormValues);
  const shouldShowSlicePicker = useSelector(
    FlightExchangeSearchSelectors.getShowSlicePicker
  );
  const tripCategory = useSelector(FlightSearchSelectors.getTripCategory);

  const goToNextStep = FlightsExchangeNavigationFlow.useGoToNextStep(
    CoreFlightExchangeSteps.HOME
  );

  const searchNextStep = useCallback(() => {
    dispatch(FlightSearchActions.setFlightSearch(search));
    setSearchUpdated(true);
  }, [dispatch, search]);

  useEffect(() => {
    if (searchUpdated) {
      if (shouldShowSlicePicker) {
        setShowSlicePicker(true);
      } else {
        dispatch(setOutboundSelection(ExchangeActionEnum.Change));
        if (tripCategory === TripCategory.ROUND_TRIP) {
          dispatch(setReturnSelection(ExchangeActionEnum.Change));
        }
        goToNextStep();
      }
      setSearchUpdated(false);
    }
  }, [
    searchUpdated,
    shouldShowSlicePicker,
    tripCategory,
    goToNextStep,
    dispatch,
  ]);

  const handleContinue = useCallback(
    (selected: SliceOpt[]) => {
      const outboundSelection = selected[0];
      const returnSelection = selected[1];

      const outboundSelectionAction: ExchangeActionEnum =
        outboundSelection.checked || outboundSelection.disabled
          ? ExchangeActionEnum.Change
          : ExchangeActionEnum.Keep;
      const returnSelectionAction: ExchangeActionEnum =
        returnSelection.checked || returnSelection.disabled
          ? ExchangeActionEnum.Change
          : ExchangeActionEnum.Keep;

      dispatch(setOutboundSelection(outboundSelectionAction));
      dispatch(setReturnSelection(returnSelectionAction));
      goToNextStep();
    },
    [dispatch, goToNextStep]
  );

  return (
    <>
      <Page className={clsx(block, className)}>
        <div className={cn("container")}>
          <div className={cn("content")}>
            <FlightExchangeLanding onSearch={searchNextStep} />
            {children}
          </div>
        </div>
      </Page>
      {selectedFlightItinerary.data && (
        <FlightSlicePickerModal
          flight={selectedFlightItinerary.data}
          isOutboundDisabled={false}
          isReturnDisabled={false}
          open={showSlicePicker}
          onClose={() => setShowSlicePicker(false)}
          onContinue={handleContinue}
        />
      )}
    </>
  );
};
